import React, { useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { kioskStore } from '~/stores/kiosk.store';
import { syncStore } from '~/stores';
import { KioskKeyboard } from './KioskKeyboard';
import KioskSettingsButton from './KioskSettingsButton';
import {
  KioskSequenceDetector,
  EFFLUX_KIOSK_SEQUENCES,
} from '~/utils/KioskSequenceDetector';
import {
  lockTizenKioskKeys,
  setTizenPanelKeyLock,
  setTizenUSBLock,
  getTizenKioskCapabilities,
} from '~/services/platform/tizenKiosk';

const KioskOverlay = styled.div<{ $cursorVisible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9998;
  background: transparent;
  /* 2026-06-08 — Cursor visível em browser pra operador conseguir mirar o
     canto superior esquerdo no 7-click. Em Android/Tizen/webOS o cursor
     é invisível por padrão (touch/remote), mantém comportamento original. */
  cursor: ${(p) => (p.$cursorVisible ? 'default' : 'none')};
`;

const SecretArea = styled.div`
  /* v1.0.112 — SecretArea ampliada pra FAIXA horizontal completa.
     Antes era 150x150 no canto sup. esq. — se o body sofresse qualquer
     deslocamento vertical (zoom residual, scroll do html, IME fantasma),
     a área ficava inalcançável. Agora cobre TODA a largura do topo +
     200px de altura: mesmo se o conteúdo subir 100px o operador ainda
     atinge a área. Touch-action: none extra pra blindar contra zoom. */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: rgba(0, 0, 0, 0.01);
  z-index: 10000;
  touch-action: none;
`;

/**
 * KioskGuard
 *
 * Intercepta interações do usuário quando o kiosk está bloqueado.
 * Monitora "7-click" no canto sup. esquerdo ou tecla de info pra acionar o
 * fluxo de desbloqueio.
 *
 * Fluxo (KIOSK_PASSWORD_PLAN Fase 3):
 * - Sem PIN cadastrado → 7 cliques desbloqueiam direto (back-compat).
 * - Com PIN cadastrado → 7 cliques abrem o teclado, que valida bcrypt offline.
 *
 * 2026-05-24 — Catch-22 fix (Option A): a SecretArea também renderiza em estado
 * degenerado do app (splash/sync/erro/sem-conteúdo), independente de
 * {@code kioskStore.isLocked}. Sem isso, no boot offline o operador ficava
 * preso na tela "Sem acesso ao servidor Efflux" sem como acionar o 7-tap pra
 * sair do Lock Task nativo (cenário: tablet provisionado como Device Owner +
 * server inacessível → isLocked=false porque serverEnabled nunca foi true
 * nessa instância, mesmo com Lock Task aplicado pela sessão anterior).
 *
 * - KioskOverlay (input blocker full-screen, cursor: none): apenas quando
 *   {@code isLocked} — não interfere em dev/Browser.
 * - SecretArea (150×150 canto sup. esquerdo): quando {@code isLocked} OU app
 *   em estado degenerado (sync != 'success' ou sem conteúdo). Em dev/Browser
 *   normal o app rapidamente chega em status='success' + hasContent, e a
 *   SecretArea some.
 */
const KioskGuard: React.FC<{
  children: React.ReactNode;
  onUnlock?: () => void;
}> = observer(({ children, onUnlock }) => {
  // Só o setter é usado (handler de click); o valor é lido só dentro do
  // próprio setter callback via state update — não precisa render-binding.
  const [, setClickCount] = useState(0);
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // v1.0.130 (Renato 2026-06-02): debounce contra duplicação touchstart+click.
  // Samsung One UI 7/Android 16 ignora preventDefault no touchstart em alguns
  // WebViews, fazendo cada toque físico contar 2x (touchstart + click sintético
  // ~300ms depois) — 3 toques físicos virava 6+ contagens → unlock disparava
  // antes dos 7 esperados. 350ms filtra o click sintético sem prejudicar
  // cliques humanos rápidos (~3/s max).
  const lastSecretActionMsRef = useRef<number>(0);
  const isLocked = kioskStore.isLocked;
  // Desktop (wrapper Tauri): o kiosk NATIVO (teclado 100% bloqueado) sobe
  // TRAVADO no boot, independente do kioskStore (server-driven). Mantemos a
  // SecretArea (7-click do mouse) sempre disponível enquanto o nativo estiver
  // travado; ao desbloquear, avisamos o wrapper (kiosk_set_locked=false) pra
  // soltar o teclado + normalizar a janela.
  const isDesktopKiosk =
    typeof window !== 'undefined' &&
    !!(window as unknown as { __TAURI__?: unknown }).__TAURI__;
  const [nativeLocked, setNativeLocked] = useState(isDesktopKiosk);
  // Estado degenerado: app sem sync concluído ou sem conteúdo pra tocar.
  // Inclui idle/syncing(first)/error/auth_error e também success-sem-conteúdo.
  // Não usamos só syncStore.status === 'error' porque o operador também precisa
  // de saída na tela de splash inicial (status='idle' ou 'syncing' indefinido).
  const appInDegradedState =
    syncStore.status !== 'success' || !syncStore.hasContent;
  const shouldShowSecretArea = isLocked || appInDegradedState || nativeLocked;

  // Ponto ÚNICO de "desbloqueou". No desktop (Tauri) solta o teclado no wrapper
  // nativo (kiosk_set_locked=false) e esconde a SecretArea; sempre chama o
  // onUnlock do pai. Best-effort — falha do IPC não trava a UI.
  const notifyUnlocked = useCallback(() => {
    if (isDesktopKiosk) {
      setNativeLocked(false);
      const core = (
        window as unknown as {
          __TAURI__?: {
            core?: { invoke?: (cmd: string, args?: unknown) => unknown };
          };
        }
      ).__TAURI__?.core;
      try {
        void core?.invoke?.('kiosk_set_locked', { locked: false });
      } catch {
        /* best-effort */
      }
    }
    if (onUnlock) onUnlock();
  }, [onUnlock, isDesktopKiosk]);

  const handleSecretAction = useCallback(() => {
    // v1.0.130 — debounce filtra eventos duplicados (touchstart + click
    // sintético do mesmo toque físico no Samsung One UI 7 / Android 16).
    const now = Date.now();
    const delta = now - lastSecretActionMsRef.current;
    if (delta < 350) {
      console.log(
        `[KioskGuard] Duplicate event filtered (Δ=${delta}ms < 350ms)`,
      );
      return;
    }
    lastSecretActionMsRef.current = now;

    setClickCount((prev) => {
      const next = prev + 1;

      console.log(`[KioskGuard] Secret action progress: ${next}/7`);

      if (next >= 7) {
        if (kioskStore.requiresPinForUnlock) {
          console.log('[KioskGuard] 🔑 7-click → opening PIN keyboard');
          setKeyboardOpen(true);
        } else {
          console.log(
            '[KioskGuard] 🔓 7-click → unlocking direct (no PIN configured)',
          );
          kioskStore.unlock();
          notifyUnlocked();
        }
        return 0;
      }

      return next;
    });

    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
    }
    clickTimerRef.current = setTimeout(() => {
      setClickCount(0);
    }, 2000);
  }, [notifyUnlocked]);

  // 2026-06-01 — Detector de sequência (cross-platform).
  //
  // ⚠️ ORDEM CRÍTICA (v1.0.106): este useEffect DEVE ficar ANTES do
  // useEffect do lockTizenKioskKeys. Ambos usam `keydown` capture phase
  // — listeners no mesmo phase executam na ordem de registro, e o detector
  // precisa LER a tecla ANTES do blocker chamar stopPropagation.
  //
  // Combos suportados:
  //   - VOL+×3 → VOL-×3 → OK (em 5s) — universal, qualquer controle Samsung
  //   - INFO ×7 (em 3s) — alternativa pra controles com tecla INFO
  // Match → dispara o mesmo fluxo do 7-tap touch (PIN se configurado, ou
  // unlock direto). Não bloqueia toques na SecretArea — é additive.
  useEffect(() => {
    if (!shouldShowSecretArea) return;
    if (keyboardOpen) return;

    const detector = new KioskSequenceDetector(
      EFFLUX_KIOSK_SEQUENCES,
      (sequenceLabel) => {
        console.log(
          `[KioskGuard] 🔓 sequência casada: ${sequenceLabel} → ativando handleSecretAction(×7 equivalente)`,
        );
        if (kioskStore.requiresPinForUnlock) {
          setKeyboardOpen(true);
        } else {
          kioskStore.unlock();
          notifyUnlocked();
        }
      },
    );
    return detector.attach();
  }, [shouldShowSecretArea, keyboardOpen, notifyUnlocked]);

  // Desktop (Tauri) — listener LEGADO do evento `kiosk:show-pin`. O wrapper não
  // usa mais atalho de teclado pra destravar (teclado 100% bloqueado; unlock é
  // só pelo mouse via SecretArea acima). Mantido inerte por compatibilidade —
  // no-op em Tizen/Android/browser e nunca disparado no wrapper atual.
  useEffect(() => {
    interface TauriEventApi {
      event?: {
        listen?: (
          name: string,
          handler: (e: { payload?: unknown }) => void,
        ) => Promise<() => void>;
      };
    }
    const tauri = (window as unknown as { __TAURI__?: TauriEventApi })
      .__TAURI__;
    const listen = tauri?.event?.listen;
    if (!listen) return;

    let unlisten: (() => void) | null = null;
    let cancelled = false;
    void listen('kiosk:show-pin', (e) => {
      // payload === false = re-travando (fecha keypad); true/undefined = abrir.
      if (e?.payload === false) {
        setKeyboardOpen(false);
        return;
      }
      if (kioskStore.requiresPinForUnlock) {
        setKeyboardOpen(true);
      } else {
        kioskStore.unlock();
        notifyUnlocked();
      }
    }).then((fn) => {
      if (cancelled) fn();
      else unlisten = fn;
    });

    return () => {
      cancelled = true;
      if (unlisten) unlisten();
    };
  }, [notifyUnlocked]);

  // 2026-06-01 (v1.0.106) — Lock Tizen nativo:
  // (1) registra teclas perigosas do controle remoto via tvinputdevice
  // (2) blocker em capture phase bloqueia TODAS as teclas
  //     (preventDefault + stopPropagation) — vol não mexe no volume real,
  //     info não abre painel debug, enter/setas inertes, exit/home inertes.
  //     O detector acima já leu a tecla ANTES (capture, registrado primeiro).
  // (3) setPanelKeyLock(true) em SSSP pra travar botões físicos do painel
  // No-op em Android/browser. Idempotente. Auto-cleanup no unmount/unlock.
  //
  // Depende também da plataforma: com o kiosk ligado persistido, o boot já nasce
  // isLocked=true e este efeito (filho) roda ANTES do AppContent (pai) setar
  // window.__effluxPlatform — lockTizenKioskKeys via "não é Tizen", virava no-op
  // e nunca re-rodava, deixando a TV destravada a partir do 2º boot. Quando a
  // plataforma chega (kioskStore.setPlatform), o efeito roda de novo e trava.
  const kioskPlatform = kioskStore.platform;
  useEffect(() => {
    if (!isLocked) {
      void setTizenPanelKeyLock(false);
      return;
    }
    const cleanup = lockTizenKioskKeys();
    void setTizenPanelKeyLock(true);
    // v1.0.110 (Onda 1.3) — Auto-lock USB junto com kiosk. Em Consumer e
    // SSSP sem Partner Cert: no-op silencioso (vide TizenB2BResult).
    void setTizenUSBLock(true);
    // v1.0.110 (Onda 1.1) — Log capabilities Tizen lazy
    void getTizenKioskCapabilities();
    return () => {
      cleanup();
      void setTizenPanelKeyLock(false);
      void setTizenUSBLock(false);
    };
  }, [isLocked, kioskPlatform]);

  // Drena fila de auditoria quando o keyboard fecha (pode ter eventos novos)
  useEffect(() => {
    if (keyboardOpen) return;
    return () => {
      syncStore
        .drainKioskAuditQueue()
        .catch((err) =>
          console.warn('[KioskGuard] Drain audit queue failed:', err),
        );
    };
  }, [keyboardOpen]);

  // v1.0.111 — Tab A7 zoom-glitch defensive reset.
  // Mesmo com setSupportZoom(false) no WebView, em algumas combinações de
  // Samsung One UI + Accessibility settings o zoom pode escapar.
  // Sempre que o keyboard PIN fecha (ou ao montar/desmontar o guard),
  // forçamos o body de volta pra scroll 0 e zoom natural — garantia
  // adicional contra o sintoma "vídeo subiu deixando barra preta embaixo".
  useEffect(() => {
    if (keyboardOpen) return;
    // Reset scroll do body (caso algum focus tenha causado scrollIntoView)
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    }
  }, [keyboardOpen]);

  // v1.0.112 / v1.0.114 — Watchdog GLOBAL + telemetria pesada + escape hatch.
  //
  // Renato (2026-06-01): após v1.0.111 e v1.0.112, sintoma persistiu —
  // vídeo subiu E SecretArea inalcançável mesmo com 200px full-width +
  // overflow:hidden. Isso descarta "scroll do body". Hipóteses ativas:
  //   - <video> element com double-tap-zoom interno (Android WebView quirk)
  //   - Transform CSS aplicado em ancestral (vira containing block dos
  //     descendentes position:fixed — eles "viajam" junto)
  //   - VisualViewport encolhe (IME fantasma) sem layout viewport mudar
  //   - WindowInsets do Android One UI deslocando o WebView
  //
  // Defesas:
  //   1-3. (v1.0.112) gesture/multi-touch preventDefault + scroll reset
  //   4. (v1.0.114) Telemetria: quando watchdog detecta QUALQUER anomalia
  //      (transform inesperado, visualViewport != innerHeight, bounds
  //      negativos), captura state completo via console.error pra cair
  //      em logsCapture/crashCapture.
  //   5. (v1.0.114) Escape hatch GLOBAL: 10 cliques em QUALQUER lugar da
  //      tela em 5s → unlock direto. Não depende da SecretArea.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const resetViewport = () => {
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      document.documentElement.scrollTop = 0;
    };

    // Defesa 1: bloquear gesture-zoom nativo do WebView
    const blockGesture = (e: Event) => {
      e.preventDefault();
    };
    // Defesa 2: bloquear pinch-zoom multi-touch (2+ dedos simultâneos)
    const blockMultiTouch = (e: TouchEvent) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    };

    document.addEventListener('gesturestart', blockGesture, { passive: false });
    document.addEventListener('gesturechange', blockGesture, {
      passive: false,
    });
    document.addEventListener('gestureend', blockGesture, { passive: false });
    document.addEventListener('touchmove', blockMultiTouch, { passive: false });

    // Defesa 3: watchdog que detecta deslocamento + captura state pra debug
    const captureViewportState = (reason: string) => {
      try {
        const html = document.documentElement;
        const body = document.body;
        const root = document.getElementById('root');
        const video = document.querySelector('video');
        const htmlRect = html.getBoundingClientRect();
        const bodyRect = body.getBoundingClientRect();
        const rootRect = root?.getBoundingClientRect();
        const videoRect = video?.getBoundingClientRect();
        const htmlStyle = window.getComputedStyle(html);
        const bodyStyle = window.getComputedStyle(body);
        const rootStyle = root ? window.getComputedStyle(root) : null;
        const visualVP = window.visualViewport;

        // Esse console.error vira entry persistente em logsCapture (release).
        console.error(
          `[KioskGuard.drift] ${reason}\n` +
            ` window: innerW=${window.innerWidth} innerH=${window.innerHeight} ` +
            `scrollY=${window.scrollY} devicePR=${window.devicePixelRatio}\n` +
            ` visualVP: w=${visualVP?.width} h=${visualVP?.height} ` +
            `offT=${visualVP?.offsetTop} offL=${visualVP?.offsetLeft} ` +
            `scale=${visualVP?.scale}\n` +
            ` <html>: rect={x:${htmlRect.x} y:${htmlRect.y} w:${htmlRect.width} h:${htmlRect.height}} ` +
            `transform=${htmlStyle.transform} scrollTop=${html.scrollTop}\n` +
            ` <body>: rect={x:${bodyRect.x} y:${bodyRect.y} w:${bodyRect.width} h:${bodyRect.height}} ` +
            `transform=${bodyStyle.transform} scrollTop=${body.scrollTop}\n` +
            ` #root: rect=${rootRect ? `{x:${rootRect.x} y:${rootRect.y} w:${rootRect.width} h:${rootRect.height}}` : 'null'} ` +
            `transform=${rootStyle?.transform}\n` +
            ` <video>: rect=${videoRect ? `{x:${videoRect.x} y:${videoRect.y} w:${videoRect.width} h:${videoRect.height}}` : 'null'}`,
        );
      } catch (err) {
        console.warn('[KioskGuard.drift] captureState failed', err);
      }
    };

    const watchdog = window.setInterval(() => {
      const bodyTop = document.body.scrollTop;
      const docTop = document.documentElement.scrollTop;
      const winY = window.scrollY;
      const bodyRect = document.body.getBoundingClientRect();
      const htmlRect = document.documentElement.getBoundingClientRect();

      // Detecta scroll OU body/html com bounds Y negativos (deslocaram pra cima)
      const hasScrollDrift = bodyTop !== 0 || docTop !== 0 || winY !== 0;
      const hasBodyYDrift = bodyRect.y < -1; // -1 pra tolerar arredondamento
      const hasHtmlYDrift = htmlRect.y < -1;
      // VisualViewport menor que innerHeight = IME-like adjustment
      const visualVP = window.visualViewport;
      const hasVisualDrift =
        visualVP !== undefined &&
        visualVP !== null &&
        Math.abs(visualVP.height - window.innerHeight) > 2;

      if (hasScrollDrift || hasBodyYDrift || hasHtmlYDrift || hasVisualDrift) {
        captureViewportState(
          `drift detected: scroll=${hasScrollDrift} bodyY=${hasBodyYDrift}(${bodyRect.y.toFixed(1)}) htmlY=${hasHtmlYDrift}(${htmlRect.y.toFixed(1)}) visual=${hasVisualDrift}`,
        );
        resetViewport();
        // Defesa adicional: força layout do body de volta ao topo
        document.body.style.transform = '';
        document.documentElement.style.transform = '';
      }
    }, 500);

    return () => {
      document.removeEventListener('gesturestart', blockGesture);
      document.removeEventListener('gesturechange', blockGesture);
      document.removeEventListener('gestureend', blockGesture);
      document.removeEventListener('touchmove', blockMultiTouch);
      window.clearInterval(watchdog);
    };
  }, []);

  // v1.0.114 → REMOVIDO v1.0.118 — Escape hatch GLOBAL desabilitado.
  //
  // Tinha sido adicionado quando o vídeo "subia" no Tab A7 e a SecretArea
  // ficava inalcançável (bug viewport-drift do v1.0.110-v1.0.113). Mas após
  // os fixes de zoom/transform e a SecretArea estendida pra faixa horizontal
  // 200px no topo (v1.0.112), o escape hatch ficou OBSOLETO E PERIGOSO:
  // qualquer cliente tocando 10× sem querer no display (criança, encostar,
  // limpeza) destrava o kiosk sozinho. Renato reportou em prod
  // (2026-06-02). Confiança na SecretArea + ↑×3↓×3OK do controle TV
  // como vias únicas de destravar.

  const handleKeyboardUnlocked = useCallback(() => {
    setKeyboardOpen(false);
    notifyUnlocked();
  }, [notifyUnlocked]);

  // Referência estável pro KioskKeyboard. Antes era arrow inline, que
  // causava re-render do effect interno do keyboard (deps mudavam).
  const handleKeyboardClose = useCallback(() => {
    setKeyboardOpen(false);
  }, []);

  return (
    <>
      {children}
      {/* KioskOverlay: input blocker full-screen apenas quando kiosk locked.
          Mantém cursor: none no modo loja. NÃO renderiza em dev mode ou em
          estado degenerado sem kiosk — a SecretArea abaixo cobre essa saída
          de emergência sem bloquear o resto da UI. */}
      {isLocked && !keyboardOpen && (
        <KioskOverlay $cursorVisible={kioskStore.platform === 'browser'} />
      )}
      {/* SecretArea (150×150 canto sup. esquerdo): visível tanto no kiosk
          locked quanto no estado degenerado. Sai assim que app vira
          status='success' + hasContent (caminho feliz dev / playback normal
          sem kiosk). */}
      {shouldShowSecretArea && !keyboardOpen && (
        <SecretArea
          // BUG: em mobile, um único toque dispara onTouchStart E
          // onClick sintético (~300ms depois). Sem e.preventDefault no
          // touchstart, 4 toques físicos contam como 8 → abre o teclado
          // antes do esperado E o click sintético do último toque cai
          // no Backdrop do KioskKeyboard, fechando ele imediatamente.
          // preventDefault cancela a geração do click sintético; mantemos
          // onClick pra cobrir mouse desktop.
          onTouchStart={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleSecretAction();
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleSecretAction();
          }}
        />
      )}
      {keyboardOpen && (
        <KioskKeyboard
          onClose={handleKeyboardClose}
          onUnlocked={handleKeyboardUnlocked}
        />
      )}
      {/* v1.0.142 — botão "Abrir Configurações" (Android only). Self-gate:
          aparece só em Android + kiosk OFF, canto inferior direito. Saída
          determinística pra Settings sem depender de nav bar / Home. */}
      <KioskSettingsButton />
    </>
  );
});

export default KioskGuard;
